import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useMemo } from 'react';

import { SessionReportCard } from '@/components/Cards';
import { Icon } from '@/components/Icon';
import { IconRotate, IconSize } from '@/components/Icon/types';
import styles from '@/components/SessionSection/SessionSection.module.css';
import { Tag } from '@/components/Tag/Tag';

import { convertDate } from '@/utils/convertFirebaseDate';
import { SessionTypeInDatabase } from '@/utils/getFirebaseSessionTypes';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { FirebaseTimestamp, Session, SessionStatus } from '@/models';

interface SessionSectionProps {
  session: Session;
  sessionIndex: number;
  totalSessions: number;
  sessionType: SessionTypeInDatabase | undefined;
  isActive: boolean;
  onToggle: () => void;
  handleRetryUpload: (sessionId: string) => void;
}

export const SessionSection = ({
  session,
  sessionIndex,
  totalSessions,
  sessionType,
  isActive,
  onToggle,
  handleRetryUpload,
}: SessionSectionProps) => {
  const sessionDate = convertDate(session.date as FirebaseTimestamp);

  const hasError = useMemo(() => session.status.toLowerCase() == SessionStatus.Error.toLowerCase(), [session.status]);

  const colorForSessionType = () => {
    switch (sessionType?.title.toLowerCase()) {
      case 'gait observations':
        return 'blue';
      case 'knee stability':
        return 'green';
      default:
        return 'yellow';
    }
  };

  return (
    <IonGrid className={mergeClassNames(styles.sessionSection, hasError && styles.error, 'ion-no-padding')}>
      {hasError && (
        <IonRow className={styles.errorHeader}>
          <div className={styles.errorLeft}>
            <Icon name='error' />
            <span className={styles.errorTitle}>
              The upload failed because of a weak or missing internet connection.
            </span>
          </div>

          <button className={styles.retryButton} onClick={() => handleRetryUpload(session.id)}>
            Retry
          </button>
        </IonRow>
      )}

      <IonRow className={styles.sessionSectionHeader} onClick={onToggle}>
        <IonCol className={styles.headerLeft} size='11'>
          <span className={styles.sessionTitle}>{`Session ${totalSessions - sessionIndex}: `}</span>
          <span className={styles.sessionDate}>{sessionDate}</span>
          {sessionType?.title && <Tag variant={colorForSessionType()}>{sessionType.title}</Tag>}
        </IonCol>
        <IonCol className={styles.chevronContainer} size='1'>
          <Icon
            color='var(--ion-color-grey400)'
            direction={isActive ? IconRotate.North : IconRotate.South}
            name='chevron'
            size={IconSize.medium}
          />
        </IonCol>
      </IonRow>

      {isActive && session.reportInfo && Object.keys(session.reportInfo).length > 0 && (
        <>
          {Object.keys(session.reportInfo).map((key: any, index: number, array: string[]) => (
            <div
              className={mergeClassNames(
                styles.contentWrapper,
                isActive && styles.open,
                array.length > 1 && index !== array.length - 1 && styles.marginBottom,
              )}
              key={key}>
              <SessionReportCard
                clientId={session.clientId}
                date={new Date(session.reportInfo![key].uploadTimestamp)}
                id={session.reportInfo![key].id ?? ''}
                name={session.reportInfo![key].reportName}
                reportInfo={session.reportInfo![key]}
                reportType={session.reportInfo![key].reportType}
                tags={session.reportInfo![key].tags}
              />
            </div>
          ))}
        </>
      )}

      {session.reportInfo?.length === 0 && (
        <IonRow className='ion-align-items-center'>
          <IonCol className={mergeClassNames(styles.contentWrapper, isActive && styles.open)} size='12'>
            <h6 className={styles.noSessionsFound}>No reports available</h6>
          </IonCol>
        </IonRow>
      )}
    </IonGrid>
  );
};
